// Faithful port of RMS's report-card canvas layer renderers.
// Source: mpa-rms/frontend/src/pages/admin/ReportCardDesignerEdit.jsx
//         + components/reports/{subject-block-styled,inline-markup,report-gradient-presets}.
//
// RMS renders every layer body in physical units (pt for fonts, mm for
// spacing) inside a 794x1123 (A4 @96dpi) canvas that is then scaled with a
// single CSS transform. We mirror that exactly so designer == RMS == print.
//
// Only the data-source plumbing differs from RMS:
//   • images: RMS uses layer.url; we resolve our S3 key (layer.src) via assetUrls
//   • campus header/logo: resolved against the scoped branch (tpl)
import { useLayoutEffect, useRef } from "react"

type AnyLayer = Record<string, any>
export type RmsTpl = {
  schoolName?: string
  schoolTagline?: string
  headerColor?: string
  accentColor?: string
  logoKey?: string
  headerImageKey?: string
}

// ── helpers ──────────────────────────────────────────────────────────────
const rmsUrlToKey = (url: string) => {
  const s = String(url)
  if (s.startsWith("rc-assets/")) return s   // already an S3 key (picker)
  return "rc-assets/" + s.replace(/^\/+/, "").replace(/^report-card-templates\//, "")
}

export const gradientTextStyle = (gradient?: string): Record<string, any> => {
  if (!gradient) return {}
  return {
    backgroundImage: gradient,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
    color: "transparent",
  }
}

const MARKUP_RE = /(\*\*[^*\n]+?\*\*|__[^_\n]+?__|\*[^*\n]+?\*|\^[^\^\n]+?\^)/g
type Seg = { text: string; bold?: boolean; italic?: boolean; underline?: boolean; superscript?: boolean }
export const parseInlineMarkup = (text?: string): Seg[] => {
  if (!text) return []
  const parts = String(text).split(MARKUP_RE)
  const segments: Seg[] = []
  for (const part of parts) {
    if (part === "" || part == null) continue
    if (part.startsWith("**") && part.endsWith("**") && part.length >= 4) segments.push({ text: part.slice(2, -2), bold: true })
    else if (part.startsWith("__") && part.endsWith("__") && part.length >= 4) segments.push({ text: part.slice(2, -2), underline: true })
    else if (part.startsWith("*") && part.endsWith("*") && part.length >= 3) segments.push({ text: part.slice(1, -1), italic: true })
    else if (part.startsWith("^") && part.endsWith("^") && part.length >= 3) segments.push({ text: part.slice(1, -1), superscript: true })
    else segments.push({ text: part })
  }
  return segments
}

const FAIL_GRADES = new Set(["F", "U", "AB"])
const GRADE_PALETTE: Record<string, any> = {
  "A*": { outer: "#3d5ee1", inner: "#dfe6ff", letterColor: "#eef2ff", stroke: "#2e3192" },
  "A+": { outer: "#3d5ee1", inner: "#dfe6ff", letterColor: "#eef2ff", stroke: "#2e3192" },
  A: { outer: "#10b981", inner: "#a7f3d0", letterColor: "#ecfdf5", stroke: "#064e3b" },
  B: { outer: "#0ea5e9", inner: "#bae6fd", letterColor: "#eef2ff", stroke: "#1e3a8a" },
  C: { outer: "#3d5ee1", inner: "#dfe6ff", letterColor: "#eef2ff", stroke: "#2e3192" },
  D: { outer: "#4f6add", inner: "#eef2ff", letterColor: "#eef2ff", stroke: "#2e3192" },
  E: { outer: "#3d5ee1", inner: "#dfe6ff", letterColor: "#eef2ff", stroke: "#7f1d1d" },
}
const FAIL_PALETTE = { outer: "#dc2626", inner: "#fecaca", letterColor: "#fff1f2", stroke: "#7f1d1d" }
const paletteForGrade = (letterRaw: string, layer: AnyLayer) => {
  const letter = (letterRaw || "").toUpperCase()
  if (FAIL_GRADES.has(letter)) {
    return {
      outer: layer.failOuterColor || FAIL_PALETTE.outer,
      inner: layer.failInnerColor || FAIL_PALETTE.inner,
      letterColor: layer.failLetterColor || FAIL_PALETTE.letterColor,
      stroke: layer.failLetterStroke || FAIL_PALETTE.stroke,
    }
  }
  const override = (layer.gradePalette && layer.gradePalette[letter]) || {}
  const builtin = GRADE_PALETTE[letter] || GRADE_PALETTE["A"]
  return {
    outer: override.outer || builtin.outer,
    inner: override.inner || builtin.inner,
    letterColor: override.letterColor || builtin.letterColor,
    stroke: override.stroke || builtin.stroke,
  }
}

const BLURB_BY_GRADE: Record<string, string> = {
  "A*": "Passed with Excellence!", "A+": "Passed with Excellence!", A: "Outstanding Effort!",
  B: "Great work, keep it up!", C: "Good progress!", D: "Satisfactory effort.",
  E: "Keep practising!", F: "Needs improvement.", U: "Needs improvement.",
  AB: "Marked absent — please consult the class teacher.",
}
const blurbForGrade = (g: string) => BLURB_BY_GRADE[(g || "").toUpperCase()] || "Keep working hard!"

const DEFAULT_GRADE_COLORS: Record<string, string> = {
  "A*": "#15803d", A: "#16a34a", "A+": "#15803d", B: "#0891b2", "B+": "#0891b2", "B-": "#0891b2",
  C: "#3d5ee1", "C+": "#3d5ee1", "C-": "#3d5ee1", D: "#3d5ee1", "D+": "#3d5ee1", E: "#dc2626",
  F: "#991b1b", U: "#991b1b", O: "#15803d", AB: "#71717a", NA: "#71717a",
}
const gradeColorFor = (letter: string) => {
  const key = String(letter || "").toUpperCase().trim()
  if (!key || key === "—") return "#71717a"
  return DEFAULT_GRADE_COLORS[key] || "#71717a"
}

const BODY_FONT = '"Trebuchet MS", "Comic Sans MS", system-ui, sans-serif'

// ── AutoFitTextBox ──────────────────────────────────────────────────────
function AutoFitTextBox({ layer, content }: { layer: AnyLayer; content: string }) {
  const outerRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const outer = outerRef.current, inner = innerRef.current
    if (!outer || !inner) return
    if (!layer.autoFit) { inner.style.fontSize = ""; return }
    const maxPt = Number(layer.fontSize) || 12
    let s = maxPt
    inner.style.fontSize = `${s}pt`
    for (let i = 0; i < 200; i += 1) {
      if (inner.scrollHeight <= outer.clientHeight && inner.scrollWidth <= outer.clientWidth) break
      s -= 0.5
      if (s <= 6) break
      inner.style.fontSize = `${s}pt`
    }
  })
  const segs = parseInlineMarkup(content)
  return (
    <div ref={outerRef} style={{
      width: "100%", height: "100%", display: "flex", alignItems: "center",
      justifyContent: layer.textAlign === "center" ? "center" : layer.textAlign === "right" ? "flex-end" : "flex-start",
      padding: "0 2mm",
      background: layer.bg && layer.bg !== "transparent" ? layer.bg : undefined,
      border: layer.borderWidth ? `${layer.borderWidth}mm solid ${layer.borderColor || "#1c1917"}` : undefined,
      borderRadius: layer.borderRadius != null ? `${layer.borderRadius}mm` : undefined,
      overflow: "hidden", pointerEvents: "none", boxSizing: "border-box",
    }}>
      <div ref={innerRef} style={{
        fontSize: `${layer.fontSize || 12}pt`, fontWeight: layer.fontWeight || 700,
        color: layer.color || "#1f2937", textAlign: layer.textAlign || "left",
        fontFamily: layer.fontFamily || BODY_FONT, lineHeight: 1.15,
        whiteSpace: layer.autoWrap === false ? "nowrap" : "pre-line", width: "100%",
        ...gradientTextStyle(layer.gradient),
      }}>
        {segs.length ? segs.map((s, i) => (
          s.superscript
            ? <sup key={i} style={{ fontSize: "0.7em", lineHeight: 0 }}>{s.text}</sup>
            : <span key={i} style={{ fontWeight: s.bold ? "bold" : undefined, fontStyle: s.italic ? "italic" : undefined, textDecoration: s.underline ? "underline" : undefined }}>{s.text}</span>
        )) : content}
      </div>
    </div>
  )
}

// ── GradeBadge ──────────────────────────────────────────────────────────
function GradeBadge({ letter, layer }: { letter: string; layer: AnyLayer }) {
  const { outer, inner, letterColor, stroke } = paletteForGrade(letter, layer)
  return (
    <div style={{ position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
      <div style={{
        width: "88%", aspectRatio: "1 / 1", borderRadius: "50%",
        background: `radial-gradient(circle at 30% 25%, ${inner} 0%, ${outer} 75%)`,
        border: `0.6mm solid ${outer}`,
        boxShadow: "inset 0 -1mm 1.5mm rgba(0,0,0,0.18), 0 0.5mm 1mm rgba(0,0,0,0.18)",
        display: "flex", alignItems: "center", justifyContent: "center", position: "relative",
        containerType: "size" as any,
      }}>
        <span style={{
          fontFamily: '"Fredoka One", "Bungee", "Comic Sans MS", "Trebuchet MS", system-ui, sans-serif',
          fontWeight: 900, fontSize: "60cqh", lineHeight: 0.9, color: letterColor,
          background: `linear-gradient(180deg, ${letterColor} 0%, ${stroke} 100%)`,
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
          WebkitTextStroke: `2cqh ${stroke}`, paintOrder: "stroke fill", letterSpacing: "-0.04em",
        }}>{letter || "—"}</span>
      </div>
    </div>
  )
}

// ── AttendanceBar ───────────────────────────────────────────────────────
function AttendanceBarPreview({ layer }: { layer: AnyLayer }) {
  const present = 180, total = 195
  const ratio = total > 0 ? Math.min(1, present / total) : 0
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: "0.6mm", padding: "0.5mm 1mm", fontFamily: BODY_FONT, pointerEvents: "none", boxSizing: "border-box" }}>
      {layer.showLabel && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: `${layer.labelFontSize || 7.5}pt`, fontWeight: 700, color: layer.labelColor || "#2e3192", flexShrink: 0 }}>
          <span>☀️</span><span>{layer.labelText || "Attendance Journey"}</span><span>🌥</span>
        </div>
      )}
      <div style={{ flex: 1, minHeight: "1.5mm", width: "100%", background: layer.trackColor || "#e5e7eb", border: `0.2mm solid ${layer.borderColor || "#2e3192"}`, borderRadius: `${layer.borderRadius ?? 1.5}mm`, overflow: "hidden" }}>
        <div style={{ width: `${ratio * 100}%`, height: "100%", background: layer.barColor || "#4ade80" }} />
      </div>
      {layer.showRatio && (
        <div style={{ textAlign: "center", fontSize: `${(layer.labelFontSize || 7.5) - 1}pt`, color: layer.labelColor || "#3f1d0c", flexShrink: 0 }}>{present} / {total}</div>
      )}
    </div>
  )
}

// ── Congrats ────────────────────────────────────────────────────────────
function CongratsPreview({ layer }: { layer: AnyLayer }) {
  const grade = layer.sampleGrade || "A*"
  const name = layer.sampleName || "Zarmeen"
  const blurb = blurbForGrade(grade)
  const isFail = ["F", "U", "AB"].includes(String(grade).toUpperCase())
  const tail = isFail ? `Stay strong, ${name}.` : `Congratulations, ${name}!`
  return (
    <div style={{
      width: "100%", height: "100%", display: "flex", alignItems: "center",
      justifyContent: layer.textAlign === "center" ? "center" : layer.textAlign === "right" ? "flex-end" : "flex-start",
      padding: "0 2mm", fontSize: `${layer.fontSize || 14}pt`, fontWeight: layer.fontWeight || 800,
      fontStyle: layer.italic === false ? "normal" : "italic", color: layer.color || "#2e3192",
      textAlign: layer.textAlign || "center", fontFamily: layer.fontFamily || BODY_FONT,
      ...gradientTextStyle(layer.gradient), lineHeight: 1.15, overflow: "hidden", pointerEvents: "none",
    }}>{blurb} {tail}</div>
  )
}

// ── Styled subject body (Grade 1-5) ─────────────────────────────────────
function SubjectBlockStyledBody({ layer, sample }: { layer: AnyLayer; sample: AnyLayer }) {
  const accent = layer.accentColor || "#0d6e6e"
  const accentDark = layer.accentDarkColor || "#054b4b"
  const showProgress = layer.progressBars !== false
  const showBottom = layer.showBottomBar !== false
  const cells = sample?.cells || []
  const baseSize = layer.fontSize || 9
  const subjectName = sample?.name || "—"
  const noSubcats = cells.length <= 1
  const totalBarPct = Number.isFinite(Number(layer.totalBarWidthPct)) ? Math.max(20, Math.min(100, Number(layer.totalBarWidthPct))) : 100
  const totalRightPadMm = noSubcats ? 20 : 17
  const totalMinHeightMm = noSubcats ? 9 : 6
  const fmtNum = (n: any) => (n == null || n === "" ? "—" : String(n))
  const gradeHex = gradeColorFor(String(sample?.grade || ""))
  const badgeSizeMm = Number.isFinite(Number(layer.badgeSizeMm)) ? Math.max(6, Math.min(30, Number(layer.badgeSizeMm))) : 15
  const badgeRightOffsetMm = Number.isFinite(Number(layer.badgeRightOffsetMm)) ? Math.max(-15, Math.min(20, Number(layer.badgeRightOffsetMm))) : -2
  const badgeBorderWidthMm = Number.isFinite(Number(layer.badgeBorderWidthMm)) ? Math.max(0, Math.min(2, Number(layer.badgeBorderWidthMm))) : 0.4
  const badgeShowObtained = layer.badgeShowObtained !== false
  const badgeShowMax = layer.badgeShowMax === true
  const badgeShowGrade = layer.badgeShowGrade !== false
  const totalShowMax = layer.totalShowMax !== false
  const obtainedScale = Number.isFinite(Number(layer.badgeObtainedScale)) ? Math.max(0.4, Math.min(3, Number(layer.badgeObtainedScale))) : 1.4
  const maxScale = Number.isFinite(Number(layer.badgeMaxScale)) ? Math.max(0.2, Math.min(2, Number(layer.badgeMaxScale))) : 0.55
  const gradeScaleFs = Number.isFinite(Number(layer.badgeGradeScale)) ? Math.max(0.2, Math.min(2, Number(layer.badgeGradeScale))) : 0.75
  let badgeBackground: string, badgeRing = gradeHex, badgeTextColor = "#ffffff", badgeTextShadow: string, badgeBoxShadow: string
  const badgeStyle = layer.badgeStyle || "metallic"
  if (badgeStyle === "flat") {
    badgeBackground = gradeHex; badgeTextShadow = "0 0 0.3mm rgba(0,0,0,0.4)"; badgeBoxShadow = "0 0.3mm 0.6mm rgba(0,0,0,0.2)"
  } else if (badgeStyle === "outline") {
    badgeBackground = "#ffffff"; badgeTextColor = gradeHex; badgeTextShadow = "none"; badgeBoxShadow = "0 0.2mm 0.4mm rgba(0,0,0,0.15)"
  } else {
    badgeBackground = `radial-gradient(circle at 30% 25%, rgba(255,255,255,0.65) 0%, ${gradeHex} 55%, rgba(0,0,0,0.25) 100%)`
    badgeTextShadow = "0 0 0.4mm rgba(0,0,0,0.7)"
    badgeBoxShadow = "inset 0 0.3mm 0.6mm rgba(255,255,255,0.55), 0 0.5mm 0.9mm rgba(0,0,0,0.35)"
  }
  if (layer.badgeBorderColor) badgeRing = layer.badgeBorderColor
  if (layer.badgeTextColor) badgeTextColor = layer.badgeTextColor
  return (
    <div style={{
      width: "100%", boxSizing: "border-box", background: layer.bodyBg || "#ffffff",
      borderRadius: layer.cardRadiusMm ? `${layer.cardRadiusMm}mm` : "2mm", fontFamily: layer.bodyFontFamily || BODY_FONT,
      fontSize: `${baseSize}pt`, display: "flex", flexDirection: "column", overflow: "visible", pointerEvents: "none",
      position: "relative", border: layer.cardBorder === false ? "none" : `${layer.cardBorderWidthMm ?? 0.2}mm solid ${layer.cardBorderColor || "rgba(0,0,0,0.06)"}`,
    }}>
      {layer.showHeader !== false && (
        <div style={{ background: accent, color: layer.headerColor || "#ffffff", borderRadius: "99mm", border: `${layer.headerBorderWidthMm ?? 0.4}mm solid ${layer.headerBorderColor || "#ffffff"}`, padding: "0.7mm 2mm", margin: "1mm", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 0.2mm 0.5mm rgba(0,0,0,0.18)" }}>
          <div style={{ textAlign: "center", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.25mm", fontSize: `${baseSize + 1}pt`, lineHeight: 1.1 }}>{subjectName}</div>
        </div>
      )}
      {layer.showSubcats !== false && cells.length > 0 && (
        <div style={{ padding: "0.6mm 1.5mm 0.4mm 1.5mm", color: layer.rowColor || "#1f2937", display: "flex", flexDirection: "column", gap: "0.8mm" }}>
          {cells.map(([desc, max, val]: any[], i: number) => {
            const maxNum = Number(max), valNum = Number(val)
            const pct = Number.isFinite(maxNum) && Number.isFinite(valNum) && maxNum > 0 ? Math.max(0, Math.min(100, (valNum / maxNum) * 100)) : 0
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: showProgress ? "1fr 8mm 25% 8mm" : "1fr 8mm 8mm", alignItems: "center", gap: "1.2mm", padding: "0.1mm 0" }}>
                <div style={{ fontStyle: "italic", fontWeight: 600, textTransform: "capitalize" }}>{desc}</div>
                <div style={{ fontFamily: "monospace", fontWeight: 800, textAlign: "right", color: layer.rowColor || "#1f2937" }}>{fmtNum(val)}</div>
                {showProgress && (
                  <div style={{ position: "relative", height: "2.4mm", borderRadius: "99mm", background: accentDark, overflow: "hidden", border: `0.2mm solid ${accent}` }}>
                    <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: `${pct}%`, background: "linear-gradient(90deg, rgba(255,255,255,0.85), rgba(255,255,255,0.55))", borderRadius: "99mm" }} />
                  </div>
                )}
                <div style={{ border: `0.25mm solid ${accent}`, borderRadius: "99mm", padding: "0.1mm 0.5mm", textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: accent, background: "#ffffff" }}>{fmtNum(max)}</div>
              </div>
            )
          })}
        </div>
      )}
      {showBottom && layer.showTotal !== false && (
        <div style={{ display: "flex", justifyContent: "flex-start", padding: "0 1mm 1mm 1mm", flexShrink: 0 }}>
          <div style={{ width: `${totalBarPct}%`, position: "relative", background: accentDark, color: "#ffffff", padding: `0.9mm ${totalRightPadMm}mm 0.9mm 2.5mm`, display: "flex", alignItems: "center", fontWeight: 800, borderRadius: "1.5mm", minHeight: `${totalMinHeightMm}mm`, boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: "1.6mm" }}>
              <div style={{ fontStyle: "italic", fontWeight: 800, fontSize: `${baseSize + 1}pt`, letterSpacing: "0.1mm" }}>Total</div>
              {totalShowMax && (<>
                <div style={{ opacity: 0.55, fontSize: `${baseSize}pt`, lineHeight: 1 }} aria-hidden>·</div>
                <div style={{ fontFamily: "monospace", fontWeight: 900, fontSize: `${baseSize + 1}pt`, letterSpacing: "0.05mm" }}>{fmtNum(sample?.max)}</div>
              </>)}
            </div>
            {layer.showGradeBadge !== false && (
              <div style={{ position: "absolute", right: `${badgeRightOffsetMm}mm`, top: "50%", transform: "translateY(-50%)", width: `${badgeSizeMm}mm`, height: `${badgeSizeMm}mm`, borderRadius: "50%", background: badgeBackground, border: badgeBorderWidthMm > 0 ? `${badgeBorderWidthMm}mm solid ${badgeRing}` : "none", boxShadow: badgeBoxShadow, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: badgeTextColor, textShadow: badgeTextShadow, padding: "0.4mm", boxSizing: "border-box", fontFamily: '"Trebuchet MS", system-ui, sans-serif' }}>
                {badgeShowObtained && <div style={{ fontSize: `${Math.round(obtainedScale * 100)}%`, fontWeight: 900, lineHeight: 1, fontFamily: 'Georgia, "Times New Roman", serif' }}>{fmtNum(sample?.total)}</div>}
                {badgeShowMax && <div style={{ fontSize: `${Math.round(maxScale * 100)}%`, fontWeight: 700, lineHeight: 1.05, opacity: 0.95 }}>/ {fmtNum(sample?.max)}</div>}
                {badgeShowGrade && <div style={{ fontSize: `${Math.round(gradeScaleFs * 100)}%`, fontWeight: 900, lineHeight: 1, marginTop: "0.2mm", letterSpacing: "0.2mm" }}>{fmtNum(sample?.grade)}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Legacy subject table (Playgroup / Montessori) ───────────────────────
function SubjectTablePreview({ layer }: { layer: AnyLayer }) {
  const sample = layer.sampleSubject || { name: "—", total: "—", max: "—", grade: "—", cells: [] }
  if (layer.variant === "styled") return <SubjectBlockStyledBody layer={layer} sample={sample} />
  const cells = sample.cells || []
  const headerSize = layer.headerFontSize ?? (layer.fontSize || 8) + 2
  const headerWeight = layer.headerFontWeight ?? 800
  const gradeOnly = !!layer.gradeOnly
  return (
    <div style={{ width: "100%", boxSizing: "border-box", background: layer.bodyBg || "#ffffff", border: `${layer.borderWidth ?? 0.3}mm solid ${layer.borderColor || "#cbd5e1"}`, borderRadius: "1.5mm", overflow: "hidden", fontFamily: BODY_FONT, fontSize: `${layer.fontSize || 8}pt`, display: "flex", flexDirection: "column", pointerEvents: "none" }}>
      {layer.showHeader !== false && (
        <div style={{
          background: layer.headerBg && layer.headerBg !== "transparent" ? layer.headerBg : (layer.headerBg === "transparent" ? "transparent" : "#0ea5e9"),
          color: layer.headerColor || "#eef2ff", fontWeight: headerWeight, fontSize: `${headerSize}pt`, textAlign: layer.headerTextAlign || "center",
          padding: `${layer.headerPadMm ?? 0.5}mm 1mm`, flexShrink: 0,
          textTransform: (() => { const c = layer.headerCase || (layer.headerUpper ? "upper" : "none"); return c === "upper" ? "uppercase" : c === "lower" ? "lowercase" : c === "title" ? "capitalize" : "none" })() as any,
        }}>{sample.name}</div>
      )}
      {!gradeOnly && layer.showSubcats !== false && (
        <div style={{ flex: 1, overflow: "hidden", padding: "0.4mm 1mm", color: layer.rowColor || "#1f2937" }}>
          {cells.map(([desc, max, val]: any[], i: number) => {
            const pill = layer.pillCells
            const pillStyle: any = { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: `${layer.pillColMm ?? 14}mm`, padding: "0.3mm 1mm", borderRadius: "99mm", border: `0.18mm solid ${layer.pillBorderColor || "#7dd3fc"}`, background: layer.pillBg || "#ffffff", color: layer.pillColor || layer.rowColor || "#1f2937", fontFamily: "monospace", fontWeight: 800, fontSize: "90%", whiteSpace: "nowrap", opacity: layer.pillOpacity == null ? 1 : layer.pillOpacity }
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: pill ? `1fr ${layer.pillColMm ?? 14}mm ${layer.pillColMm ?? 14}mm` : "1fr 14mm", alignItems: "center", justifyItems: pill ? "center" : "stretch", gap: pill ? "1mm" : 0, padding: pill ? "0.5mm 0" : "0.1mm 0", borderBottom: pill ? "none" : `${layer.rowLineWidth ?? 0.08}mm solid ${layer.rowLineColor || "rgba(0,0,0,0.07)"}` }}>
                <div style={{ fontWeight: 600, justifySelf: "start", textTransform: "capitalize" }}>{desc}</div>
                {pill ? (<><span style={{ ...pillStyle, width: "100%" }}>{max}</span><span style={{ ...pillStyle, width: "100%" }}>{val}</span></>) : (<div style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 800 }}>{val}/{max}</div>)}
              </div>
            )
          })}
        </div>
      )}
      {!gradeOnly && layer.showTotal !== false && (
        <div style={{ display: "flex", alignItems: "center", gap: "1.5mm", background: layer.pillCells ? "transparent" : (layer.totalBg || "#eef2ff"), color: layer.totalColor || "#2e3192", fontWeight: 800, padding: "0.4mm 1mm", flexShrink: 0 }}>
          <span>Total</span>
          {layer.pillCells ? (
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0.5mm 3mm", borderRadius: "99mm", border: `0.2mm solid ${layer.pillBorderColor || "#7dd3fc"}`, background: layer.totalBg || "#eef2ff", color: layer.totalColor || "#2e3192", fontFamily: "monospace", fontWeight: 900 }}>{sample.total}/{sample.max}</span>
          ) : (<span style={{ fontFamily: "monospace" }}>{sample.total}/{sample.max}</span>)}
        </div>
      )}
      {(gradeOnly || layer.showGrade !== false) && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: gradeOnly ? "2mm" : "0.5mm", flexGrow: gradeOnly ? 1 : 0, flexShrink: 0, flexBasis: gradeOnly ? 0 : "auto" }}>
          {layer.showGradeBadge !== false ? (
            <div style={{ width: gradeOnly ? "12mm" : "4.5mm", height: gradeOnly ? "12mm" : "4.5mm" }}><GradeBadge letter={sample.grade} layer={{ ...layer, showStarAccent: false }} /></div>
          ) : (<span style={{ fontWeight: 800, color: layer.totalColor || "#2e3192" }}>Grade: {sample.grade}</span>)}
        </div>
      )}
    </div>
  )
}

// Pick the per-campus banner entry matching the scoped school name.
const pickCampusEntry = (byC: Record<string, any> | undefined, schoolName?: string) => {
  if (!byC) return null
  const entries = Object.values(byC)
  if (!entries.length) return null
  const tokens = String(schoolName || "").toLowerCase().split(/\s+/).filter(w => w.length >= 3 && w !== "the" && w !== "academy")
  if (tokens.length) {
    const hit = entries.find((e: any) => {
      const hay = `${e?.url || ""} ${e?.text || ""}`.toLowerCase()
      return tokens.some(t => hay.includes(t))
    })
    if (hit) return hit
  }
  return entries[0]
}

// ── Main dispatcher ─────────────────────────────────────────────────────
// Renders ONLY the inner body of a layer; the Canvas wrapper handles
// absolute %-positioning, rotation, opacity and selection outline.
export function RmsLayerBody({
  layer, assetUrls, tpl, onNeedUrl,
}: {
  layer: AnyLayer
  assetUrls: Record<string, string>
  tpl: RmsTpl
  onNeedUrl?: (key: string) => void
}) {
  const need = (key?: string | null) => { if (key && !assetUrls[key] && onNeedUrl) onNeedUrl(key) }

  switch (layer.type) {
    case "text": {
      return <AutoFitTextBox layer={layer} content={layer.sample || layer.text || layer.content || ""} />
    }
    case "image": {
      need(layer.src)
      const url = layer.src ? assetUrls[layer.src] : null
      // Auto-height images (no stored heightPct) render at natural aspect; fixed
      // ones stretch to fill their box (RMS uses objectFit:'fill', never cover).
      const auto = !Number.isFinite(layer.heightPct)
      return url
        ? <img src={url} alt="" draggable={false} style={{ width: "100%", height: auto ? "auto" : "100%", objectFit: auto ? "contain" : "fill", pointerEvents: "none", display: "block" }} />
        : <div style={{ width: "100%", height: auto ? 40 : "100%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}><i className="ti ti-photo" /></div>
    }
    case "rms_photo": {
      return <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: layer.bg || "#f5f5f4", border: `${layer.borderWidth || 0.4}mm solid ${layer.borderColor || "#2e3192"}`, borderRadius: `${layer.borderRadius ?? 2}mm`, color: "#9ca3af", fontSize: "14pt", pointerEvents: "none" }}>👤</div>
    }
    case "rms_campus_logo": {
      const key = tpl.logoKey
      need(key)
      const url = key ? assetUrls[key] : null
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: layer.bg && layer.bg !== "transparent" ? layer.bg : (url ? "transparent" : "#eff6ff"), border: layer.borderWidth ? `${layer.borderWidth}mm solid ${layer.borderColor || "#3b82f6"}` : (url ? undefined : "1px dashed rgba(59,130,246,0.55)"), borderRadius: `${layer.borderRadius ?? 1}mm`, color: "#60a5fa", fontSize: "14pt", pointerEvents: "none", overflow: "hidden" }}>
          {url ? <img src={url} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: (layer.objectFit || "contain") as any, display: "block" }} /> : "🏫"}
        </div>
      )
    }
    case "rms_campus_header": {
      // Resolution mirrors RMS print: per-campus banner image for the scoped
      // branch → branch header image → school-name text.
      const mode = layer.bannerMode || "auto"
      let imgUrl: string | null = null
      let text = ""
      if (mode === "text") {
        text = layer.bannerText || tpl.schoolName || ""
      } else {
        const entry = mode === "per-campus" ? pickCampusEntry(layer.bannerByCampus, tpl.schoolName) : null
        if (entry?.mode === "text" && entry.text) text = entry.text
        else {
          const rawUrl = (entry?.mode === "image" && entry.url) ? entry.url : (layer.bannerImageUrl || null)
          const key = rawUrl ? rmsUrlToKey(rawUrl) : (tpl.headerImageKey || null)
          need(key)
          imgUrl = key ? assetUrls[key] : null
          if (!imgUrl) text = tpl.schoolName || ""
        }
      }
      const previewText = text || layer.sample || tpl.schoolName || "CAMPUS HEADER"
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: layer.bg && layer.bg !== "transparent" ? layer.bg : "transparent", border: layer.borderWidth ? `${layer.borderWidth}mm solid ${layer.borderColor || "#2e3192"}` : (imgUrl ? undefined : "1px dashed rgba(46,49,146,0.45)"), borderRadius: layer.borderRadius != null ? `${layer.borderRadius}mm` : 0, pointerEvents: "none", overflow: "hidden", boxSizing: "border-box" }}>
          {imgUrl ? <img src={imgUrl} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: (layer.objectFit || "contain") as any, display: "block" }} /> : <AutoFitTextBox layer={layer} content={previewText} />}
        </div>
      )
    }
    case "rms_attendance_bar": return <AttendanceBarPreview layer={layer} />
    case "rms_grade_badge":    return <GradeBadge letter={layer.sampleGrade || "A"} layer={layer} />
    case "rms_congrats":       return <CongratsPreview layer={layer} />
    case "rms_subject_table":  return <SubjectTablePreview layer={layer} />
    case "rms_marks_table":    return <SubjectTablePreview layer={layer} />
    default:
      return null
  }
}

export const isRmsType = (t: string) =>
  t === "text" || t === "image" || t.startsWith("rms_")
